import type { DashboardViews, WidgetInstance } from '@contracts';
import { authedFetch } from '@/lib/apiClient';

import { BRIDGE_URL as BRIDGE } from '@/lib/config';

/**
 * Tablero guardado del usuario (BridgeHub → Odoo), POR VISTA. `null` si nunca
 * guardó. Retrocompat: el doc viejo `{ widgets: [...] }` se interpreta como la
 * vista `ventas`.
 */
export async function loadDashboard(): Promise<DashboardViews | null> {
  try {
    const res = await authedFetch(`${BRIDGE}/dashboards/me`);
    if (!res.ok) return null;
    const body = await res.json();
    if (!body?.success) return null;
    const doc = body.data as { views?: DashboardViews; widgets?: WidgetInstance[] } | null;
    if (!doc) return null;
    if (doc.views) return doc.views;
    if (doc.widgets) return { ventas: doc.widgets }; // doc legacy
    return null;
  } catch {
    return null;
  }
}

/** Persiste las vistas tocadas en UN request (el BFF hace merge, sin pisar las otras). */
/** Capa donde guardar: 'user' (mi vista) o 'company' (default de la compañía; solo admin,
 *  el BFF lo gatea). `companyId` es requerido para 'company'. */
export interface SaveScope {
  scope?: 'user' | 'company';
  companyId?: number;
}

export async function saveDashboard(views: DashboardViews, scope: SaveScope = {}): Promise<void> {
  await authedFetch(`${BRIDGE}/dashboards/me`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ views, ...scope }),
  });
}

/**
 * Restaurar. Sin opts: quita MI override → caigo a compañía/sistema (cualquier rol). Con
 * `{ scope:'company', companyIds }`: borra el default publicado de esas compañías (solo admin).
 */
export async function deleteDashboard(opts?: { scope: 'company'; companyIds?: number[] }): Promise<DashboardViews | null> {
  let url = `${BRIDGE}/dashboards/me`;
  if (opts?.scope === 'company') {
    const params = new URLSearchParams({ scope: 'company' });
    (opts.companyIds ?? []).forEach((id) => params.append('companyIds', String(id)));
    url += `?${params.toString()}`;
  }
  const res = await authedFetch(url, { method: 'DELETE' });
  if (!res.ok) return null;
  const body = await res.json();
  // El DELETE devuelve ya las vistas resueltas (compañía/sistema) → sin GET extra.
  return body?.success ? ((body.data?.views as DashboardViews) ?? null) : null;
}
